<template>
  <app-view-default-template class="app-view-default-template-reports-and-presentations">

    <app-content-title label="Reports & Presentations"></app-content-title>

    <!-- Filters, date year select, category select -->
    <div class="filters-wrapper">
      <b-row>
        <b-col xs="12" sm="6" md="4" lg="3">
          <b-form-select v-model="yearSelect.selected" :options="yearSelect.options" size="md" @change="onChange()"></b-form-select>
        </b-col>

        <b-col xs="12" sm="6" md="4" lg="3">
          <b-form-select v-model="categorySelect.selected" :options="categorySelect.options" size="md" @change="onChange()"></b-form-select>
        </b-col>

        <b-col xs="12" sm="6" md="4" lg="3">
          <b-button variant="outline-secondary" size="md" v-on:click="resetFilters">Clear</b-button>
        </b-col>
      </b-row>
    </div>

    <div class="items-wrapper">
      <b-row>
        <b-col xs="12" sm="6" md="4" lg="3" v-for="(item, index) in items" v-bind:key="index">
          <div class="item-wrapper">
            <a :href="item.ContentDownloadUrl" target="_blank">
              <div>
                <div class="item-bg-wrapper">
                  <div class="item-bg-inner" :class="getCategoryColorClass(item.CategoryName)">
                    <b-icon :icon="getCategoryIcon(item.CategoryName)"></b-icon>

                    <div class="download-btn">
                      <b-icon icon="download"></b-icon>
                    </div>
                  </div>
                </div>

                <div class="date">
                  <p>{{ date(item.PublishTime) }}</p>
                </div>

                <div class="title">
                  <h6>{{ item.Title }}</h6>
                </div>
              </div>
            </a>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Infinite scroll component -->
    <infinite-loading :identifier="infiniteId" @infinite="infiniteHandler" :distance="20">
      <template slot="no-more">
        <p>You've reached the end.</p>
      </template>

      <template slot="no-results">
        <p>Unfortunately no results.</p>
      </template>

      <template slot="error">
        <p>
          Oops, unfortunately something went wrong. :(
        </p>

        <app-button-primary text="Try again" v-on:click.native="resetFilters"></app-button-primary>
      </template>
    </infinite-loading>

  </app-view-default-template>
</template>

<script>
  // Templates
  import AppViewDefaultTemplate from '@/components/layout/templates/view/AppViewTemplateDefault';
  import AppContentTitle from '@/components/ui/titles/AppContentTitle';

  // Components
  import InfiniteLoading from 'vue-infinite-loading';

  // Api
  import PrintedMaterialApi from "@/services/printed-material";

  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';

  // Buttons
  import AppButtonPrimary from '@/components/ui/buttons/AppButtonPrimary';

  // Configs
  import PrintedMaterialConfig from '@/configs/printed-material';
  import ReportsAndPresentationConfig from '@/configs/reports-and-presentation';
  import DatesConfig from '@/configs/dates';

  const IPO_START_DATE        = process.env.VUE_APP_IPO_START_DATE;
  const IPO_START_DATE_FORMAT = DatesConfig.IPO_START_DATE_FORMAT;
  const API_PAGE_INDEX        = PrintedMaterialConfig.INDEX_API;
  const API_PAGE_SIZE         = PrintedMaterialConfig.PAGE_SIZE_API;
  const API_PAGE_SIZE_DEFAULT = PrintedMaterialConfig.PAGE_SIZE_API;
  const API_LANGUAGE_CODE     = PrintedMaterialConfig.LANGUAGE_CODE_API;
  const AVAILABLE_CATEGORIES  = ReportsAndPresentationConfig.CATEGORIES;

  export default {
    name: 'ReportsAndPresentations',

    mixins: [mixinDatesHandler],

    components: {
      AppViewDefaultTemplate, AppContentTitle, InfiniteLoading, AppButtonPrimary,
    },

    data() {
      return {
        // API
        items: [],
        pageIndex: API_PAGE_INDEX,
        pageSize: API_PAGE_SIZE,
        pageSizeDefault: API_PAGE_SIZE_DEFAULT,

        // Infinite scroll
        infiniteId: 1,

        yearSelect: {
          selected: null,
          options: []
        },

        categorySelect: {
          selected: null,
          options: [
            { value: null, text: 'Filter by category' },
          ]
        }
      }
    },

    methods: {
      onChange() {
        this.clearResults();
        this.triggerInfiniteLoader();

        this.queryHandler();
      },

      triggerInfiniteLoader() {
        this.infiniteId += 1;
      },

      infiniteHandler($state) {
        this.pageIndex += 1;

        const data = {
          params: {
            languageCode: API_LANGUAGE_CODE,
            startDate: this.startDate,
            endDate: this.endDate,
            pageIndex: this.pageIndex - 1, // Subtract 1, the start index for this API is 0
            pageSize: this.pageSize,
            category: this.categorySelect.selected,
          }
        };

        this.resetViewHandler();

        let _this = this;
        new PrintedMaterialApi(data, {}).get(function (response) {
          if (response.status !== 200) {
            $state.error();
            return;
          }

          // For infinite scroll, API does not provide total rows/items, so if we get empty response we now we are done.
          if (!response.data.Items.length > 0) {
            _this.pageIndex -= 1;
            $state.complete();
            return
          }

          _this.items.push(...response.data.Items);

          $state.loaded();
        });
      },

      clearResults() {
        this.items     = [];
        this.pageIndex = 0;
      },

      resetFilters() {
        this.yearSelect.selected     = null;
        this.categorySelect.selected = null;

        this.clearResults();

        this.triggerInfiniteLoader();
      },

      date(dateTime) {
        if (!this._mixinDateHandler_IsValidDate(dateTime)) {
          return '';
        }

        return this._mixinDateHandler_ApiResponseDateTimeToFormat(dateTime, DatesConfig.UI_DEFAULT_DATE_FORMAT, true);
      },

      getYearsSelectDefaultOptions() {
        return [
          { value: null, text: 'Filter by year' },
        ];
      },

      generateYears() {
        const startYear = this.$moment(IPO_START_DATE, IPO_START_DATE_FORMAT).format('Y');
        let endYear     = this.$moment().format('Y');

        const startYearInt = parseInt(startYear);
        const endYearInt   = parseInt(endYear);

        let years = [];

        for (let i = startYearInt; i <= endYearInt; i++) {
          years.push(i);
        }

        return years;
      },

      getYears() {
        const generateYears = this.generateYears();
        let years           = this.getYearsSelectDefaultOptions();

        generateYears.forEach((x) => {
          years.push({
            value: x, text: x
          });
        });

        this.yearSelect.options = years;
      },

      queryHandler() {
        const pageIndex = this.pageIndex;
        const year      = this.yearSelect.selected;
        const category  = this.categorySelect.selected;

        const query = {
          index: pageIndex,
          year: year,
          category: category,
        };

        if (!parseInt(pageIndex)) {
          delete query.index;
        }

        if (!parseInt(year)) {
          delete query.year;
        }

        if (!category) {
          delete query.category;
        }

        this.$router.push({
          query: query
        }).catch(()=>{});
      },

      viewHandler() {
        const pageIndex = this.pageIndexUrlParam;

        // Set filters
        this.yearSelect.selected     = this.pageYearUrlParam;
        this.categorySelect.selected = this.pageCategoryUrlParam;

        // Calculate page size
        this.pageSize = this.pageSize * pageIndex;

        this.triggerInfiniteLoader();
      },

      resetViewHandler() {
        if (this.pageSize !== this.pageSizeDefault) {
          this.pageSize  = this.pageSizeDefault;
          this.pageIndex = parseInt(this.$route.query.index);
        }
      },

      setCategories() {
        const categories = AVAILABLE_CATEGORIES;

        let selectCategoryOptions = [];

        categories.forEach((x) => {
          selectCategoryOptions.push({
            value: x.name,
            text: x.label,
          })
        });

        this.categorySelect.options.push(...selectCategoryOptions);
      },

      getCategoryIcon(categoryName) {
        const categories = AVAILABLE_CATEGORIES;
        let icon         = 'layout-wtf'; // Default icon

        const items = categories.filter((x) => {
          return x.name === categoryName;
        });

        if (items && items.length > 0) {
          const item = Object.assign(...items);

          if (item.icon) {
            icon = item.icon;
          }
        }

        return icon;
      },

      getCategoryColorClass(categoryName) {
        const categories = AVAILABLE_CATEGORIES;
        let color        = 'category-default-color'; // Default icon

        const items = categories.filter((x) => {
          return x.name === categoryName;
        });

        if (items && items.length > 0) {
          const item = Object.assign(...items);

          if (item.icon) {
            color = item.colorClass;
          }
        }

        return color;
      },
    },

    computed: {
      startDate() {
        return this.yearSelect.selected ? `${this.yearSelect.selected}-01-01` : null;
      },

      endDate() {
        return this.yearSelect.selected ? `${this.yearSelect.selected}-12-31` : null;
      },

      pageIndexUrlParam() {
        let index = this.$route.query.index;

        if (!parseInt(index)) {
          return 1;
        }

        return parseInt(index);
      },

      pageYearUrlParam() {
        const year  = this.$route.query.year;
        const years = this.generateYears();

        if (!years.includes(parseInt(year))) {
          return null;
        }

        return year;
      },

      pageCategoryUrlParam() {
        let category = this.$route.query.category;

        if (!category) {
          return null;
        }

        return category;
      },
    },

    created() {
      window.scrollTo({
        top: 0,
      });
    },

    mounted() {
      this.setCategories();
      this.getYears();
      this.viewHandler();
    },

    watch: {
      'pageIndex': function () {
        this.queryHandler();
      },
    }
  }
</script>

<style scoped lang="scss">
  .app-view-default-template-reports-and-presentations {
    background-color: white;
    min-height: calc(100vh - 100px);

    .filters-wrapper {
      width: 100%;
      margin-bottom: 40px;

      .btn-outline-secondary {
        background-color: transparent;
        border-color: #cecece;
        color: black;

        &:hover, &:focus, &:active, &:visited {
          background-color: black;
          color: white;
          outline: none;
        }
      }

      div[class*="col-"]{
        margin-bottom: 20px;
      }
    }

    .item-wrapper {
      margin-bottom: 60px;
      a {
        text-decoration: none;
      }

      .item-bg-wrapper {
        width: 100%;
        margin-bottom: 25px;
        position: relative;

        .item-bg-inner {
          height: 200px;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;

          svg {
            font-size: 80px;
            line-height: 80px;
            color: white;
          }

          &.category-annual-reports-color {
            background-color: #00bbd3;

            .download-btn {
              background-color: rgba(0, 187, 211, 0.5);
            }
          }

          &.category-general-meetings-color {
            background-color: #fec006;

            .download-btn {
              background-color: rgba(254, 192, 6, 0.5);
            }
          }

          &.category-ir-presentations-color {
            background-color: #6639b6;

            .download-btn {
              background-color: rgba(102, 57, 182, 0.5);
            }
          }

          &.category-other-color {
            background-color: #009587;

            .download-btn {
              background-color: rgba(0, 149, 135, 0.5);
            }
          }

          &.category-policies-color {
            background-color: #f34235;

            .download-btn {
              background-color: rgba(243, 66, 53, 0.5);
            }
          }

          &.category-quarterly-statements-color {
            background-color: #2095f2;

            .download-btn {
              background-color: rgba(32, 149, 242, 0.5);
            }
          }

          &.category-sustainability-reports-color {
            background-color: #8ac249;

            .download-btn {
              background-color: rgba(138, 194, 73, 0.5);
            }
          }

          &.category-default-color {
            background-color: #fe5621;

            .download-btn {
              background-color: rgba(254, 86, 33, 0.5);
            }
          }

          .download-btn {
            display: flex;
            width: 50px;
            height: 50px;
            position: absolute;
            bottom: -25px;
            right: 5px;
            border: 5px solid rgba(255, 255, 255, 0.99);
            border-radius: 50%;
            color: white;
            align-items: center;
            justify-content: center;

            svg {
              font-size: 25px;
            }
          }
        }
      }

      .date {
        width: 100%;

        p {
          margin-bottom: 10px;
          font-size: 12px;
          color: #757575;
        }
      }

      .title {
        width: 100%;

        h6 {
          font-size: 20px;
        }
      }

      &:hover {
        .item-bg-wrapper {
          .item-bg-inner {
            opacity: 0.80;
            transition: opacity;

            &.category-annual-reports-color {
              .download-btn {
                background-color: #00bbd3;
              }
            }

            &.category-general-meetings-color {
              .download-btn {
                background-color: #fec006;
              }
            }

            &.category-ir-presentations-color {
              .download-btn {
                background-color: #6639b6;
              }
            }

            &.category-other-color {
              .download-btn {
                background-color: #009587;
              }
            }

            &.category-policies-color {
              .download-btn {
                background-color: #f34235;
              }
            }

            &.category-quarterly-statements-color {
              .download-btn {
                background-color: #2095f2;
              }
            }

            &.category-sustainability-reports-color {
              .download-btn {
                background-color: #8ac249;
              }
            }

            &.category-default-color {
              .download-btn {
                background-color:  #fe5621;
              }
            }

            svg {
              font-size: 100px;
              transition: font-size 0.5s;
            }

            .download-btn {
              svg {
                font-size: 25px;
              }
            }
          }

          .download-btn {
            background-color: black;
          }
        }
      }
    }

    @media (min-width: 768px) {
      .filters-wrapper {
        div[class*="col-"] {
          margin-bottom: 0;
        }
      }
    }
  }
</style>
